import { useTranslation } from 'react-i18next';
import type { Booking, VenueDetail } from '../lib/api.js';
import { formatDate, formatHijri, formatTime } from '../lib/time.js';

/**
 * Confirmation.
 *
 * Two things on this screen do real work beyond reassuring the guest.
 *
 * 1. THE BOOKING REFERENCE, large and monospaced. It is read aloud at the door
 *    and down a phone, which is why its alphabet excludes I, L, O and U.
 *
 * 2. THE `wa.me` DEEP LINK, prefilled with the reference. Tapping it sends a
 *    message to the venue, and an inbound message OPENS the guest's 24-hour
 *    customer service window. That is useful in itself - the guest now has a
 *    thread to ask questions in - and it is also a cost decision: inside the
 *    window the venue can reply freely rather than paying for a template, and
 *    from 1 October 2026 the pricing difference between the two widens.
 *
 * The link is only rendered when the venue has a WhatsApp number on record.
 * Sending a guest to a number that does not answer is worse than not offering.
 */
export function ConfirmStep({
  booking,
  venue,
  locale,
}: {
  booking: Booking;
  venue: VenueDetail;
  locale: string;
}) {
  const { t } = useTranslation();

  const venueTime = {
    timezone: venue.timezone,
    serviceDayCutoffHour: 6,
    digitStyle: venue.digit_style,
  };

  const waLink = venue.whatsapp_number
    ? `https://wa.me/${venue.whatsapp_number}?text=${encodeURIComponent(
        t('widget.waMessage', { ref: booking.booking_ref }),
      )}`
    : null;

  return (
    <section className="flex flex-col gap-5 text-center">
      <div className="flex flex-col items-center gap-2">
        <span
          className="flex h-14 w-14 items-center justify-center rounded-full bg-brand-teal text-2xl text-white"
          aria-hidden
        >
          {'✓' /* i18n-ok: a check mark */}
        </span>
        <h2 className="text-lg font-semibold">{t('widget.confirmed')}</h2>
      </div>

      <div className="rounded-lg border border-slate-200 p-4">
        <p className="text-xs uppercase tracking-wide text-slate-500">
          {t('reservations.ref')}
        </p>
        {/* Forced LTR: a booking reference is a code, and a mirrored FG-8K3M2
            cannot be read back to a host over the phone. */}
        <p
          data-testid="booking-ref"
          className="font-mono text-2xl font-semibold tracking-wider"
          style={{ direction: 'ltr' }}
          data-numeric
        >
          {booking.booking_ref}
        </p>
      </div>

      <dl className="flex flex-col gap-2 text-sm">
        <div className="flex items-center justify-between">
          <dt className="text-slate-500">{t('common.date')}</dt>
          <dd className="text-end">
            {formatDate(booking.service_date, venueTime, locale)}
            <span className="block text-xs text-brand-bronze">
              {formatHijri(booking.service_date, venueTime, locale)}
            </span>
          </dd>
        </div>
        <div className="flex items-center justify-between">
          <dt className="text-slate-500">{t('common.time')}</dt>
          <dd data-numeric>{formatTime(booking.starts_at, venueTime, locale)}</dd>
        </div>
        <div className="flex items-center justify-between">
          <dt className="text-slate-500">{t('common.partySize')}</dt>
          <dd data-numeric>{booking.party_size}</dd>
        </div>
      </dl>

      {waLink ? (
        <a
          href={waLink}
          data-testid="wa-confirm"
          target="_blank"
          rel="noreferrer"
          className="rounded-lg bg-brand-teal py-3 font-medium text-white hover:bg-brand-teal-dark"
        >
          {t('widget.messageUs')}
        </a>
      ) : null}

      <a
        href={booking.manage_url}
        className="rounded-lg border border-slate-200 py-2.5 text-sm"
      >
        {t('widget.manageBooking')}
      </a>

      {/* Sets the expectation before the guest starts wondering. A booking
          that says "confirmed" but has not been acknowledged by a human is the
          most common reason someone phones to ask whether it worked. */}
      <p className="rounded-lg bg-brand-gold-light px-3 py-2.5 text-sm text-brand-ink">
        {t('widget.confirmWindow')}
      </p>

      <p className="text-xs text-slate-500">{t('widget.confirmationSent')}</p>
    </section>
  );
}
